import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { useEffect, useMemo } from "react";
import InfiniteScroll from "react-infinite-scroll-component";

import { PropertyCard, PageHeader, SearchInput } from "components/molecules";
import { ListLoader } from "components/atoms";

import {
	getProperties,
	getPropertiesCount,
	getPropertiesFilters,
	getPropertiesList,
	getPropertiesLoading,
	getPropertiesTotal,
	setPropertiesFilter,
} from "state/modules/properties";
import { Property } from "models/properties";
import { ComplexFilterItem, ComplexFilterType, SearchComplexFilter } from "interfaces/filters";
import { FONT_FAMILY_MEDIUM } from "styles/typography";
import { MAIN_THEME } from "styles/colors";

const Header = styled.div`
	display: flex;
	padding: 0 30px 30px;

	@media (max-width: 1200px) {
		padding: 0 20px 25px;
	}

	@media (max-width: 768px) {
		flex-direction: column;
		padding: 0 15px 20px
	;
	}

	@media (max-width: 480px) {
		padding: 0 10px 20px;
	}
`;

const Container = styled.div`
	overflow-y: auto;
	width: 100%;
	background: ${MAIN_THEME.DARK_BG};

	@media (max-width: 768px) {
		height: auto;
		min-height: 100vh;
	}
`;

const PropertiesList = styled(InfiniteScroll as any)`
	display: grid;
	width: 100%;
	grid-template-columns: repeat(auto-fit, 420px);
	gap: 75px;
	padding: 0 30px 100px;

	@media (max-width: 1200px) {
		grid-template-columns: repeat(auto-fit, 350px);
		gap: 50px;
		padding: 0 20px 50px;
	}

	@media (max-width: 768px) {
		grid-template-columns: 1fr;
		gap: 30px;
		padding: 0 15px 50px;
	}

	@media (max-width: 480px) {
		padding: 0 10px 30px;
	}
`;

const PropertiesTable = styled.div`
	padding: 0 30px;
	overflow: hidden;

	@media (max-width: 1200px) {
		padding: 0 20px;
	}

	@media (max-width: 768px) {
		padding: 0 15px 80px;
	}

	@media (max-width: 480px) {
		padding: 0 10px 80px;
	}
`;

const TableHeader = styled.div`
	display: grid;
	grid-template-columns: 100px 180px 1fr;
	gap: 15px;
	padding: 10px 15px;
	border-bottom: 1px solid ${MAIN_THEME.BORDER_COLOR};
	margin-bottom: 15px;

	@media (max-width: 768px) {
		display: none;
	}
`;

const HeaderText = styled.p`
	color: ${MAIN_THEME.TEXT_SECONDARY};
	font-size: 14px;
	font-weight: 500;
	margin: 0;
`;

const LoaderContainer = styled.div`
	padding: 0 30px;

	@media (max-width: 1200px) {
		padding: 0 20px;
	}

	@media (max-width: 768px) {
		padding: 0 15px;
	}

	@media (max-width: 480px) {
		padding: 0 10px;
	}
`;



const SearchWrapper = styled.div`
	width: 100%;
	max-width: 520px;

	@media (max-width: 768px) {
		max-width: 100%;
	}
`;

const EmptyListMessage = styled.p`
	font-family: ${FONT_FAMILY_MEDIUM};
	color: ${MAIN_THEME.TEXT_SECONDARY};
	font-size: 16px;
	margin: 0 30px;
	padding: 15px 10px;
	background: ${MAIN_THEME.DARK_BG_TERTIARY};
	border: 1px solid ${MAIN_THEME.BORDER_COLOR};
	border-radius: 8px;

	@media (max-width: 1200px) {
		margin: 0 20px;
	}

	@media (max-width: 768px) {
		margin: 0 15px;
	}

	@media (max-width: 480px) {
		margin: 0 10px;
		font-size: 14px;
	}
`;

const PropertiesPage = (): JSX.Element => {
	const propertiesList = useSelector(getPropertiesList);
	const propertiesCount = useSelector(getPropertiesCount);
	const propertiesTotal = useSelector(getPropertiesTotal);
	const isPropertiesLoading = useSelector(getPropertiesLoading);

	const filters = useSelector(getPropertiesFilters);

	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(getProperties());
	}, [dispatch]);

	const hasMore = useMemo(
		() => propertiesCount < propertiesTotal,
		[propertiesCount, propertiesTotal]
	);

	const loadMore = () => {
		if (!isPropertiesLoading) {
			dispatch(getProperties(true));
		}
	};

	const handleSearchChange = (updatedFilter: SearchComplexFilter) => {
		dispatch(setPropertiesFilter([updatedFilter]));
		dispatch(getProperties());
	};

	const renderLoader = () =>
		propertiesCount <= 0 && isPropertiesLoading ? (
			<LoaderContainer>
				<ListLoader />
			</LoaderContainer>
		) : null;

	const renderProperties = () => {
		if (!propertiesList.length && !isPropertiesLoading) {
			return <EmptyListMessage>Empty list</EmptyListMessage>;
		}

		return (
			<PropertiesTable>
				<TableHeader>
					<HeaderText>Image</HeaderText>
					<HeaderText>Name</HeaderText>
					<HeaderText>Address</HeaderText>
				</TableHeader>
				{propertiesList.map((property: Property) => (
					<PropertyCard key={property.id} data={property} />
				))}
			</PropertiesTable>
		);
	};

	return (
		<Container id="container">
			<div style={{ padding: "10px 30px 20px" }}>
				<PageHeader
					title="Properties"
					description="Manage your properties, filter by owner, and mute inactive ones."
				/>
			</div>
			<Header>
				<SearchInput
					filterData={filters[0] as SearchComplexFilter}
					changeFilter={handleSearchChange}
					placeholder="Search by name, address, owner"
					background="transparent"
					border={`1px solid ${MAIN_THEME.BORDER_COLOR}`}
					width="320px"
				/>
			</Header>


			{renderProperties()}
			{renderLoader()}
		</Container>
	);
};

export default PropertiesPage;
